import { useState, useContext, useMemo } from 'react'
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Box,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import {
  PatternTreeContext,
  ConditionGroupType,
  ConditionType,
} from '../../contexts/PatternTreeContext'
import { useTheme } from '@mui/material/styles'
import getConditionLabel from '../../../../common/utils/getConditionLabel'
import { Pattern } from '../../contexts/PatternContext'

// Converts a number to an alphabetic representation (A, B, C, ..., Z, AA, AB, etc.)
function getAlphabeticSuffix(index: number): string {
  if (index < 0) return ''

  const getChar = (n: number) => String.fromCharCode(65 + (n % 26)) // 65 is ASCII for 'A'

  if (index < 26) {
    return getChar(index)
  }

  // For indices >= 26, use multiple characters (AA, AB, etc.)
  let result = ''
  let value = index

  do {
    const remainder = value % 26
    result = getChar(remainder) + result
    value = Math.floor(value / 26) - 1
  } while (value >= 0)

  return result
}

// Calculate the algorithmic title for a condition group
function getAlgorithmicGroupTitle(pattern: Pattern | undefined, groupId: string): string {
  if (!pattern) return 'Group'

  const buyGroups = pattern.buyConditionGroups || []
  const sellGroups = pattern.sellConditionGroups || []

  // Use both buy and sell groups for the traversal
  const allGroups = [...buyGroups, ...sellGroups]

  // Counter for groups encountered during traversal
  let groupCounter = 0
  // Flag to indicate if target group was found
  let foundTarget = false
  // Depth of the target group when found
  let targetDepth = 0

  // Recursive function to traverse groups in depth-first order
  const traverseGroups = (groups: ConditionGroupType[], currentDepth: number = 1): void => {
    if (foundTarget) return // Stop traversal if target already found

    for (const group of groups) {
      groupCounter++

      if (group.id === groupId) {
        foundTarget = true
        targetDepth = currentDepth
        return
      }

      // Traverse nested groups (depth-first)
      if (group.conditionGroups && group.conditionGroups.length > 0) {
        traverseGroups(group.conditionGroups, currentDepth + 1)
        if (foundTarget) return // Stop if found in nested groups
      }
    }
  }

  // Start the traversal
  traverseGroups(allGroups)

  if (!foundTarget) {
    return 'Group' // Return default if group not found
  }

  // Convert to 1-based index for display and get alphabetic suffix
  const suffix = getAlphabeticSuffix(groupCounter - 1)

  return `Group ${targetDepth}${suffix}`
}

const PrerequisiteSettings = ({ condition, sourceGroupId, viewOnly = false }: any) => {
  const theme = useTheme()
  const [showInfo, setShowInfo] = useState(false)

  const { handleChangeCondition, pattern } = useContext(PatternTreeContext)

  // Get the current side (buy or sell) for this condition
  const getConditionSide = () => {
    const findInGroups = (groups: ConditionGroupType[]): 'buy' | 'sell' | null => {
      for (const group of groups) {
        if (group.conditions.some((c) => c.id === condition.id)) {
          return 'buy'
        }
        const result = findInGroups(group.conditionGroups)
        if (result) return 'buy'
      }
      return null
    }

    if (findInGroups(pattern.buyConditionGroups)) return 'buy'
    if (findInGroups(pattern.sellConditionGroups)) return 'sell'
    return null
  }

  const conditionSide = getConditionSide()

  // Build list of available conditions and groups (excluding self and circular dependencies)
  const availableOptions = useMemo(() => {
    const options: Array<{
      id: string
      type: 'condition' | 'group'
      label: string
      groupPath?: string
    }> = []

    const relevantGroups =
      conditionSide === 'buy' ? pattern.buyConditionGroups : pattern.sellConditionGroups

    if (!relevantGroups) return options

    // Build maps for circular dependency checking
    const conditionById = new Map<string, ConditionType>()
    const groupToConditionIds = new Map<string, Set<string>>()

    const collectConditions = (groups: ConditionGroupType[], path: string[] = []) => {
      for (const group of groups) {
        const nextPath = [...path, group.id]
        if (!groupToConditionIds.has(group.id)) {
          groupToConditionIds.set(group.id, new Set())
        }
        for (const cond of group.conditions || []) {
          conditionById.set(cond.id, cond)
          for (const groupId of nextPath) {
            if (!groupToConditionIds.has(groupId)) {
              groupToConditionIds.set(groupId, new Set())
            }
            groupToConditionIds.get(groupId)?.add(cond.id)
          }
        }
        if (group.conditionGroups?.length) {
          collectConditions(group.conditionGroups, nextPath)
        }
      }
    }

    collectConditions(relevantGroups)

    // Build dependency map
    const conditionDeps = new Map<string, Set<string>>()
    for (const [condId, cond] of conditionById.entries()) {
      const deps = new Set<string>()
      const prereqs = cond.settings?.prerequisites || []
      for (const prereq of prereqs) {
        if (prereq.type === 'condition') {
          deps.add(prereq.id)
        } else if (prereq.type === 'group') {
          const groupConditionIds = groupToConditionIds.get(prereq.id)
          if (groupConditionIds) {
            for (const id of groupConditionIds) {
              deps.add(id)
            }
          }
        }
      }
      conditionDeps.set(condId, deps)
    }

    // Check if startId depends on targetId (directly or transitively)
    const conditionDependsOn = (startId: string, targetId: string): boolean => {
      if (!startId || !targetId) return false
      const visited = new Set<string>()
      const stack = [startId]
      while (stack.length) {
        const current = stack.pop() as string
        if (current === targetId) return true
        if (visited.has(current)) continue
        visited.add(current)
        const deps = conditionDeps.get(current)
        if (deps) {
          for (const dep of deps) {
            if (!visited.has(dep)) stack.push(dep)
          }
        }
      }
      return false
    }

    // Check if group depends on a specific condition
    const groupDependsOnCondition = (groupId: string, conditionId: string): boolean => {
      const groupConditions = groupToConditionIds.get(groupId)
      if (!groupConditions) return false
      for (const condId of groupConditions) {
        if (conditionDependsOn(condId, conditionId)) return true
      }
      return false
    }

    const walkGroup = (group: ConditionGroupType, depth: number = 1, parentGroupLabel?: string) => {
      const algorithmicTitle = getAlgorithmicGroupTitle(pattern, group.id)
      const groupLabel = group.groupTitle
        ? `${algorithmicTitle}: ${group.groupTitle}`
        : algorithmicTitle

      // Add group as option only if it doesn't create a circular dependency
      if (!groupDependsOnCondition(group.id, condition.id)) {
        options.push({
          id: group.id,
          type: 'group',
          label: groupLabel,
          groupPath: parentGroupLabel,
        })
      }

      // Add conditions in this group
      for (const cond of group.conditions) {
        // Exclude the current condition
        if (cond.id === condition.id) continue

        // Exclude conditions that would create circular dependencies
        if (conditionDependsOn(cond.id, condition.id)) continue

        options.push({
          id: cond.id,
          type: 'condition',
          label: getConditionLabel(cond),
          groupPath: groupLabel,
        })
      }

      // Recursively walk nested groups
      for (const nestedGroup of group.conditionGroups) {
        walkGroup(nestedGroup, depth + 1, groupLabel)
      }
    }

    relevantGroups.forEach((group) => walkGroup(group, 1))

    return options
  }, [condition.id, pattern, conditionSide])

  // Get current prerequisites
  const currentPrerequisites = condition.settings?.prerequisites || []

  const isSelected = (id: string, type: 'condition' | 'group') => {
    return currentPrerequisites.some((prereq: any) => prereq.id === id && prereq.type === type)
  }

  const handleToggle = (id: string, type: 'condition' | 'group') => {
    if (viewOnly) return

    let updatedPrerequisites = [...currentPrerequisites]

    const isCurrentlySelected = isSelected(id, type)

    if (isCurrentlySelected) {
      // Remove the prerequisite
      updatedPrerequisites = updatedPrerequisites.filter(
        (prereq: any) => !(prereq.id === id && prereq.type === type)
      )
    } else {
      // Add the prerequisite
      updatedPrerequisites.push({ id, type })
    }

    handleChangeCondition(sourceGroupId, condition.id, {
      'settings.prerequisites': updatedPrerequisites.length > 0 ? updatedPrerequisites : undefined,
    })
  }

  const handleRemovePrerequisite = (id: string, type: 'condition' | 'group') => {
    if (viewOnly) return

    const updatedPrerequisites = currentPrerequisites.filter(
      (prereq: any) => !(prereq.id === id && prereq.type === type)
    )

    handleChangeCondition(sourceGroupId, condition.id, {
      'settings.prerequisites': updatedPrerequisites.length > 0 ? updatedPrerequisites : undefined,
    })
  }

  return (
    <Paper
      sx={{
        p: 2,
        maxWidth: 400,
        maxHeight: 500,
        background: theme.palette.background.custom1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body1">Prerequisites</Typography>
        <InfoIcon
          sx={{ fontSize: '1rem', color: 'text.secondary', cursor: 'pointer' }}
          onClick={() => setShowInfo(!showInfo)}
        />
      </Box>

      <Collapse in={showInfo}>
        <Typography variant="body2" color="textSecondary">
          Prerequisites enforce sequential ordering between conditions. A condition with
          prerequisites will only be met after all its prerequisite conditions or groups are met
          first.
        </Typography>
      </Collapse>

      <Box
        sx={{
          maxHeight: 300,
          overflowY: 'auto',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}
      >
        {availableOptions.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              No available options
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ p: 0 }}>
            {availableOptions.map((option) => {
              const selected = isSelected(option.id, option.type)
              return (
                <Box key={`${option.type}-${option.id}`}>
                  <ListItem
                    dense
                    onClick={viewOnly ? undefined : () => handleToggle(option.id, option.type)}
                    sx={{
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      cursor: viewOnly ? 'default' : 'pointer',
                      opacity: viewOnly ? 0.6 : 1,
                      '&:hover': {
                        backgroundColor: viewOnly ? 'transparent' : theme.palette.action.hover,
                      },
                    }}
                  >
                    <Checkbox
                      edge="start"
                      checked={selected}
                      tabIndex={-1}
                      disableRipple
                      disabled={viewOnly}
                      size="small"
                    />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{option.label}</Typography>
                          <Chip
                            label={option.type}
                            size="small"
                            sx={{
                              height: '18px',
                              fontSize: '0.65rem',
                              textTransform: 'capitalize',
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        option.groupPath ? (
                          <Typography variant="caption" color="textSecondary">
                            {option.groupPath}
                          </Typography>
                        ) : null
                      }
                    />
                  </ListItem>
                </Box>
              )
            })}
          </List>
        )}
      </Box>

      {currentPrerequisites.some(
        (prereq: any) => !availableOptions.find((o) => o.id === prereq.id && o.type === prereq.type)
      ) && (
        <Box
          sx={{ mt: 2, p: 2, backgroundColor: theme.palette.error.main + '15', borderRadius: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningIcon sx={{ fontSize: '1rem', color: theme.palette.error.main }} />
            <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
              Invalid Prerequisites Detected
            </Typography>
          </Box>
          {currentPrerequisites
            .filter(
              (prereq: any) =>
                !availableOptions.find((o) => o.id === prereq.id && o.type === prereq.type)
            )
            .map((prereq: any) => (
              <Box
                key={`invalid-${prereq.type}-${prereq.id}`}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}
              >
                <Typography variant="caption" color="error" sx={{ flex: 1 }}>
                  {prereq.type}: {prereq.id}
                </Typography>
                {!viewOnly && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemovePrerequisite(prereq.id, prereq.type)}
                    sx={{ padding: '2px' }}
                  >
                    <CloseIcon sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                )}
              </Box>
            ))}
        </Box>
      )}

      {currentPrerequisites.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="textSecondary">
            {currentPrerequisites.length} prerequisite{currentPrerequisites.length !== 1 ? 's' : ''}{' '}
            selected
          </Typography>
        </Box>
      )}
    </Paper>
  )
}

export default PrerequisiteSettings
